import { createSlice } from '@reduxjs/toolkit';
import { normalizeEventData } from '../../utils/eventStyling';

// Mock data for default page when no event is linked
export const MOCK_EVENT_DATA = {
  eventId: null,
  eventCode: 'MOCK01',
  eventName: 'حدث تجريبي',
  mainEventPhoto:
    'https://storage.googleapis.com/eventsharingphotos.firebasestorage.app/event-main-photos/clients/54FE87/039c538d-41f8-431c-ac60-2bf9433433de_IMG_6617.jpg',
  clientName: null,
  mainText: 'مرحباً بك في صفحة الحدث التجريبي. يمكنك رفع الصور والفيديوهات وإضافة التعليقات.',
  templateId: 'event',
  token: null,
  eventDate: '25 ديسمبر 2024',
  mainTextColor: '#FFFFFF',
  mainTextEffect: 'none',
  backgroundImageOpacity: '100',
  titleTextColor: '#FFFFFF',
  titleTextEffect: 'none',
  titleTextEffectColor: '#000000',
  titleFontSize: '48',
  titleFontFamily: 'default',
  subtitleTextColor: '#FFFFFF',
  subtitleTextEffect: 'none',
  subtitleTextEffectColor: '#000000',
  subtitleFontSize: '20',
  subtitleFontFamily: 'default',
  theme: 'event',
};

const initialState = {
  eventId: null,
  eventCode: null,
  eventName: null,
  mainEventPhoto: null,
  clientName: null,
  mainText: null,
  templateId: null,
  token: null,
  eventDate: null,
  mainTextColor: null,
  mainTextEffect: 'none',
  backgroundImageOpacity: '100',
  titleTextColor: '#000000',
  titleTextEffect: 'none',
  titleTextEffectColor: '#000000',
  titleFontSize: '48',
  titleFontFamily: 'default',
  subtitleTextColor: null,
  subtitleTextEffect: 'none',
  subtitleTextEffectColor: '#000000',
  subtitleFontSize: '20',
  subtitleFontFamily: 'default',
  theme: 'event',
  isInitialized: false,
  isMockMode: false,
  entryLoadState: 'idle', // idle | loading | success | error
  entryError: null,
};

const urlParamsSlice = createSlice({
  name: 'urlParams',
  initialState,
  reducers: {
    setEntryLoading: (state) => {
      state.entryLoadState = 'loading';
      state.entryError = null;
    },
    setEntryError: (state, action) => {
      state.entryLoadState = 'error';
      state.entryError = action.payload || 'Could not load this event.';
      state.isInitialized = false;
    },
    hydrateFromGuestEvent: (state, action) => {
      const normalized = normalizeEventData(action.payload);

      state.eventId = normalized.eventId;
      state.eventCode = normalized.eventCode;
      state.mainEventPhoto = normalized.mainEventPhoto;
      state.templateId = normalized.templateId;
      state.theme = normalized.theme || normalized.templateId || 'event';
      state.token = normalized.token ?? null;
      state.isInitialized = true;
      state.isMockMode = false;
      state.entryLoadState = 'success';
      state.entryError = null;
    },
    setMockEvent: (state) => {
      const normalized = normalizeEventData(MOCK_EVENT_DATA);

      state.eventId = normalized.eventId;
      state.eventCode = normalized.eventCode;
      state.eventName = normalized.eventName;
      state.mainEventPhoto = normalized.mainEventPhoto;
      state.clientName = normalized.clientName;
      state.mainText = normalized.mainText;
      state.templateId = normalized.templateId;
      state.token = normalized.token;
      state.eventDate = normalized.eventDate;
      state.mainTextColor = normalized.mainTextColor;
      state.mainTextEffect = normalized.mainTextEffect;
      state.backgroundImageOpacity = normalized.backgroundImageOpacity;
      state.titleTextColor = normalized.titleTextColor;
      state.titleTextEffect = normalized.titleTextEffect;
      state.titleTextEffectColor = normalized.titleTextEffectColor;
      state.titleFontSize = normalized.titleFontSize;
      state.titleFontFamily = normalized.titleFontFamily;
      state.subtitleTextColor = normalized.subtitleTextColor;
      state.subtitleTextEffect = normalized.subtitleTextEffect;
      state.subtitleTextEffectColor = normalized.subtitleTextEffectColor;
      state.subtitleFontSize = normalized.subtitleFontSize;
      state.subtitleFontFamily = normalized.subtitleFontFamily;
      state.theme = normalized.theme || 'event';
      state.isInitialized = true;
      state.isMockMode = true;
      state.entryLoadState = 'success';
      state.entryError = null;
    },
    setUrlParams: (state, action) => {
      const normalized = normalizeEventData(action.payload);

      state.eventId = normalized.eventId;
      state.eventCode = normalized.eventCode;
      state.eventName = normalized.eventName;
      state.mainEventPhoto = normalized.mainEventPhoto;
      state.clientName = normalized.clientName;
      state.mainText = normalized.mainText;
      state.templateId = normalized.templateId;
      state.token = normalized.token;
      state.eventDate = normalized.eventDate;
      state.mainTextColor = normalized.mainTextColor;
      state.mainTextEffect = normalized.mainTextEffect;
      state.backgroundImageOpacity = normalized.backgroundImageOpacity;
      state.titleTextColor = normalized.titleTextColor;
      state.titleTextEffect = normalized.titleTextEffect;
      state.titleTextEffectColor = normalized.titleTextEffectColor;
      state.titleFontSize = normalized.titleFontSize;
      state.titleFontFamily = normalized.titleFontFamily;
      state.subtitleTextColor = normalized.subtitleTextColor;
      state.subtitleTextEffect = normalized.subtitleTextEffect;
      state.subtitleTextEffectColor = normalized.subtitleTextEffectColor;
      state.subtitleFontSize = normalized.subtitleFontSize;
      state.subtitleFontFamily = normalized.subtitleFontFamily;
      state.theme = normalized.theme || 'event';
      state.isInitialized = true;
      state.isMockMode = false;
      state.entryLoadState = 'success';
      state.entryError = null;
    },
    clearUrlParams: (state) => {
      Object.assign(state, initialState);
    },
    updateSingleParam: (state, action) => {
      const { key, value } = action.payload;
      if (key in state) {
        state[key] = value;
      }
    },
  },
});

// Selectors
export const selectAllUrlParams = (state) => {
  const params = state.urlParams;
  // If no event is linked (not initialized or no event data), return mock data
  // but merge in the actual theme from state so theme toggle works
  if (!params.isInitialized || !params.eventCode) {
    return normalizeEventData({
      ...MOCK_EVENT_DATA,
      theme: params.theme || MOCK_EVENT_DATA.theme,
      templateId: params.templateId || MOCK_EVENT_DATA.templateId,
    });
  }
  return normalizeEventData(params);
};
export const selectEventId = (state) => state.urlParams.eventId;
export const selectEventCode = (state) => state.urlParams.eventCode;
export const selectEventName = (state) => {
  const params = state.urlParams;
  return params.eventName || (!params.isInitialized || !params.eventCode ? MOCK_EVENT_DATA.eventName : null);
};
export const selectMainEventPhoto = (state) => state.urlParams.mainEventPhoto;
export const selectClientName = (state) => state.urlParams.clientName;
export const selectMainText = (state) => {
  const params = state.urlParams;
  return params.mainText || (!params.isInitialized || !params.eventCode ? MOCK_EVENT_DATA.mainText : null);
};
export const selectTemplateId = (state) => state.urlParams.templateId;
export const selectToken = (state) => state.urlParams.token;
export const selectIsUrlParamsInitialized = (state) => state.urlParams.isInitialized;
export const selectEntryLoadState = (state) => state.urlParams.entryLoadState;
export const selectEntryError = (state) => state.urlParams.entryError;
export const selectIsMockMode = (state) => state.urlParams.isMockMode;

// Helper selector to get all params as an object
export const selectUrlParamsObject = (state) => {
  const params = state.urlParams;
  return {
    eventId: params.eventId,
    eventCode: params.eventCode,
    eventName: params.eventName,
    mainEventPhoto: params.mainEventPhoto,
    clientName: params.clientName,
    mainText: params.mainText,
    templateId: params.templateId,
    token: params.token,
    eventDate: params.eventDate,
    mainTextColor: params.mainTextColor,
    mainTextEffect: params.mainTextEffect,
    backgroundImageOpacity: params.backgroundImageOpacity,
    titleTextColor: params.titleTextColor,
    titleTextEffect: params.titleTextEffect,
    titleTextEffectColor: params.titleTextEffectColor,
    titleFontSize: params.titleFontSize,
    titleFontFamily: params.titleFontFamily,
    subtitleTextColor: params.subtitleTextColor,
    subtitleTextEffect: params.subtitleTextEffect,
    subtitleTextEffectColor: params.subtitleTextEffectColor,
    subtitleFontSize: params.subtitleFontSize,
    subtitleFontFamily: params.subtitleFontFamily,
    theme: params.theme,
  };
};

export const {
  setUrlParams,
  setMockEvent,
  hydrateFromGuestEvent,
  setEntryLoading,
  setEntryError,
  clearUrlParams,
  updateSingleParam,
} = urlParamsSlice.actions;

export default urlParamsSlice.reducer; 